import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { StyledSelectInput } from "@aureskonnect/react-ui";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { updateConfig } from "./posConfigSlice";
import {
    POSInformationModeItem,
    POSSalesModeItem,
    POSDisplayName,
} from "./constants";
import { PosCard } from "./PosCard";
import { getInfoModeIcon } from "./posIconUtils";
import { useIsItemDisabledInGeneral } from "./useIsItemDisabledInGeneral";

type SelectOption = { label: string; value: string };

/**
 * Trouve la clé dans item.active qui correspond au mode de vente (par name, id, displayName.FR ou EN).
 */
function findActiveKey(
    activeObj: Record<string, boolean>,
    sm: POSSalesModeItem
): string | undefined {
    const keys = Object.keys(activeObj);
    return keys.find(
        (k) =>
            k === sm.name ||
            k === sm.id ||
            k === sm.displayName?.FR ||
            k === sm.displayName?.EN
    );
}

interface SortableInfoCardProps {
    item: POSInformationModeItem;
    index: number;
    isActive: boolean;
    disabled: boolean;
    greyedOut: boolean;
    title: string;
    onToggle: (index: number) => void;
}

function SortableInfoCard({
    item,
    index,
    isActive,
    disabled,
    greyedOut,
    title,
    onToggle,
}: SortableInfoCardProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <PosCard
                title={title}
                active={isActive}
                isFavorite={item.isFavorite}
                disabled={disabled}
                greyedOut={greyedOut}
                icon={getInfoModeIcon(item)}
                onToggle={() => onToggle(index)}
                data-testid={`pos-information-mode-card-${item.id}`}
            />
        </div>
    );
}

export function InformationMode() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const config = useSelector((state: RootState) => state.posConfig.config);
    const isItemDisabledInGeneral = useIsItemDisabledInGeneral();
    const currentLang = (i18n.language || "fr").toUpperCase() as keyof POSDisplayName;
    const getLocalizedTitle = React.useCallback(
        (item: POSInformationModeItem) =>
            item.displayName[currentLang] || item.displayName.FR || item.name,
        [currentLang]
    );

    const saleModeOptions: SelectOption[] = React.useMemo(() => {
        const activeSaleModes = config.SalesMode.items.filter(
            (sm: POSSalesModeItem) => sm.active
        );
        return activeSaleModes
            .filter((sm: POSSalesModeItem) =>
                config.InformationMode.items.some(
                    (item: POSInformationModeItem) =>
                        findActiveKey(item.active, sm) !== undefined
                )
            )
            .map((sm: POSSalesModeItem) => ({
                label: sm.displayName[currentLang] || sm.displayName.FR || sm.name,
                value: sm.name,
            }));
    }, [config.SalesMode.items, config.InformationMode.items, currentLang]);

    const [selectedOption, setSelectedOption] = React.useState<
        SelectOption | undefined
    >(saleModeOptions[0]);

    const selectedSaleMode = selectedOption?.value || "";

    const selectedSmObj = config.SalesMode.items.find(
        (sm: POSSalesModeItem) => sm.name === selectedSaleMode
    );

    React.useEffect(() => {
        if (
            saleModeOptions.length > 0 &&
            (!selectedOption ||
                !saleModeOptions.find(
                    (o) => o.value === selectedOption.value
                ))
        ) {
            setSelectedOption(saleModeOptions[0]);
        }
    }, [saleModeOptions, selectedOption]);

    const items = config.InformationMode.items.filter(
        (item: POSInformationModeItem) =>
            selectedSmObj
                ? findActiveKey(item.active, selectedSmObj) !== undefined
                : false
    );

    const itemIds = React.useMemo(
        () => items.map((it: POSInformationModeItem) => it.id),
        [items]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleToggle = React.useCallback(
        (index: number) => {
            const item = items[index];
            if (!selectedSmObj) return;
            if (isItemDisabledInGeneral(item.id)) return;

            const activeKey = findActiveKey(item.active, selectedSmObj);
            if (!activeKey) return;

            const allItems = config.InformationMode.items;
            const realIndex = allItems.findIndex(
                (it: POSInformationModeItem) => it.id === item.id
            );
            if (realIndex === -1) return;

            const newItems = allItems.map(
                (it: POSInformationModeItem, i: number) => {
                    if (i !== realIndex)
                        return { ...it, active: { ...it.active } };
                    return {
                        ...it,
                        active: {
                            ...it.active,
                            [activeKey]: !it.active[activeKey],
                        },
                    };
                }
            );
            dispatch(
                updateConfig({
                    path: ["InformationMode", "items"],
                    value: newItems,
                })
            );
        },
        [
            items,
            selectedSmObj,
            config.InformationMode.items,
            dispatch,
            isItemDisabledInGeneral,
        ]
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const srcIndex = itemIds.indexOf(active.id as string);
            const destIndex = itemIds.indexOf(over.id as string);
            if (srcIndex < 0 || destIndex < 0) return;

            const newItems = [
                ...items.map((it: POSInformationModeItem) => ({
                    ...it,
                    active: { ...it.active },
                })),
            ];
            const [moved] = newItems.splice(srcIndex, 1);
            newItems.splice(destIndex, 0, moved);

            const otherItems = config.InformationMode.items.filter(
                (item: POSInformationModeItem) =>
                    selectedSmObj
                        ? findActiveKey(item.active, selectedSmObj) ===
                          undefined
                        : true
            );
            dispatch(
                updateConfig({
                    path: ["InformationMode", "items"],
                    value: [...newItems, ...otherItems],
                })
            );
        },
        [
            itemIds,
            items,
            config.InformationMode.items,
            selectedSmObj,
            dispatch,
        ]
    );

    return (
        <div className="h-100 pos-tab-layout-3rows__clz">
            <div className="d-flex align-items-center pos-section-header__clz">
                <div className="pos-section-title__clz">
                    {t("Information mode")}
                </div>
            </div>

            {/* Select mode de vente */}
            <div className="pos-info-select-wrapper__clz">
                <StyledSelectInput
                    id="pos-info-mode-sale-select"
                    name="pos-info-mode-sale-select"
                    options={saleModeOptions}
                    value={selectedOption || saleModeOptions[0]}
                    onChange={(option: SelectOption) =>
                        setSelectedOption(option)
                    }
                    noOptionsMessage={() =>
                        t("No sale mode with information modes")
                    }
                    placeholder={t("Select a sale mode")}
                    className="cmn_drp_changeSalesMethod"
                    data-testid="pos-info-mode-sale-select"
                />
            </div>

            {/* Cards des modes d'information pour le mode de vente sélectionné */}
            {items.length === 0 ? (
                <div className="d-flex justify-content-center align-items-center pos-empty-state__clz">
                    {t("No information mode available for this sale mode")}
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={itemIds}
                        strategy={horizontalListSortingStrategy}
                    >
                        <div
                            className="pos-cards-container__clz h-100"
                            data-testid="pos-information-mode-drag-area"
                        >
                            {items.map(
                                (
                                    item: POSInformationModeItem,
                                    index: number
                                ) => {
                                    const activeKey = selectedSmObj
                                        ? findActiveKey(item.active, selectedSmObj)
                                        : undefined;
                                    const isActive = activeKey
                                        ? item.active[activeKey] === true
                                        : false;
                                    const greyedOut = isItemDisabledInGeneral(
                                        item.id
                                    );
                                    return (
                                        <SortableInfoCard
                                            key={item.id}
                                            item={item}
                                            index={index}
                                            isActive={isActive}
                                            disabled={
                                                greyedOut ||
                                                (item.isFavorite && isActive)
                                            }
                                            greyedOut={greyedOut}
                                            title={getLocalizedTitle(item)}
                                            onToggle={handleToggle}
                                        />
                                    );
                                }
                            )}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
}
